import { Fragment, useMemo } from 'react';
import { useApp } from '../../context/AppContext.jsx';
import DataProcessor from '../../lib/dashboard/dataProcessor.js';
import DateUtils from '../../lib/dashboard/dateUtils.js';
import { COLLABORATORS, STRUCTURES } from '../../lib/dashboard/config.js';
import { getStructureBadge, getStructureClass } from '../../lib/dashboard/dashboardUtils.js';
import { BarChartBlock } from './charts/BarChartBlock.jsx';
import { TimelineChart } from './charts/TimelineChart.jsx';

function MissingDeclarations({ entries, structureId, startDate, endDate }) {
  const content = useMemo(() => {
    if (!startDate || !endDate) return null;

    const structuresToCheck = structureId
      ? (STRUCTURES[structureId] ? [STRUCTURES[structureId]] : [])
      : Object.values(STRUCTURES);

    if (!structuresToCheck.length) return null;

    const submittedIds = new Set(entries.map((e) => e.collaborator));
    const weeks = DateUtils.getWeeksInRange(startDate, endDate);
    const resultsByStructure = [];

    structuresToCheck.forEach((struct) => {
      const collabs = struct.collaborators.map((id) => COLLABORATORS[id]).filter(Boolean);
      if (!collabs.length) return;

      const totallyMissing = collabs.filter((c) => !submittedIds.has(c.id)).map((c) => c.name);
      const partiallyMissing = [];

      collabs.filter((c) => submittedIds.has(c.id)).forEach((c) => {
        const collabEntries = entries.filter((e) => e.collaborator === c.id);
        const submittedMondays = new Set(collabEntries.map((e) => DateUtils.getMonday(e.date)));
        const missingWeeks = weeks.filter((w) => !submittedMondays.has(w.mondayISO));
        if (missingWeeks.length > 0) {
          partiallyMissing.push({ name: c.name, missingWeeks });
        }
      });

      if (totallyMissing.length > 0 || partiallyMissing.length > 0) {
        resultsByStructure.push({ struct, totallyMissing, partiallyMissing });
      }
    });

    return resultsByStructure.length ? resultsByStructure : null;
  }, [entries, structureId, startDate, endDate]);

  if (!content) return null;

  return (
    <div className="table-card" id="missingDeclarations">
      <div className="card-title">Collaborateurs n&apos;ayant pas déclaré leur temps</div>
      <div style={{ padding: '14px 18px' }}>
        {content.map(({ struct, totallyMissing, partiallyMissing }) => (
          <div key={struct.id}>
            {!structureId && (
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#1A1916',
                  margin: '14px 0 8px',
                  paddingBottom: 4,
                  borderBottom: '1px solid #E5E3DC'
                }}
              >
                {struct.name}
              </div>
            )}
            {totallyMissing.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6B6860', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
                  Absence totale de déclaration
                </div>
                <div style={{ fontSize: 13, color: '#1A1916', lineHeight: 1.7, wordBreak: 'break-word' }}>
                  {totallyMissing.join(', ')}
                </div>
              </div>
            )}
            {partiallyMissing.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6B6860', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
                  Déclarations incomplètes
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 2, color: '#1A1916' }}>
                  {partiallyMissing.map(({ name, missingWeeks }) => {
                    const weekList = missingWeeks.map((w) => `semaine du ${w.label}`).join(', ');
                    const s = missingWeeks.length > 1 ? 's' : '';
                    return (
                      <li key={name}>
                        <strong>{name}</strong>
                        {`: ${weekList} manquante${s}`}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Dashboard() {
  const {
    filteredData,
    stats,
    currentStructure,
    filterFrom,
    filterTo,
    entriesForMissing
  } = useApp();

  const activityData = useMemo(() => DataProcessor.prepareActivityChartData(filteredData), [filteredData]);
  const clientData = useMemo(() => DataProcessor.prepareClientChartData(filteredData), [filteredData]);
  const timelineData = useMemo(() => DataProcessor.prepareTimelineData(filteredData), [filteredData]);
  const pivot = useMemo(() => DataProcessor.generatePivotTable(filteredData), [filteredData]);
  const collabSummary = useMemo(() => DataProcessor.prepareCollaboratorSummary(filteredData), [filteredData]);

  const avgPerCollab = stats.numberOfCollaborators > 0
    ? (stats.totalHours / stats.numberOfCollaborators).toFixed(1)
    : 0;

  const pivotTotal = Object.values(pivot).reduce((sum, data) => sum + data.total, 0);

  return (
    <div id="dashContent">
      <div className="metrics-row" id="metricsRow">
        <div className="metric-card">
          <div className="m-label">Total jours</div>
          <div className="m-value">{stats.totalHours.toFixed(1)}</div>
        </div>
        <div className="metric-card">
          <div className="m-label">Collaborateurs</div>
          <div className="m-value">{stats.numberOfCollaborators}</div>
        </div>
        <div className="metric-card">
          <div className="m-label">Clients</div>
          <div className="m-value">{stats.numberOfClients}</div>
        </div>
        <div className="metric-card">
          <div className="m-label">Moy. / collab.</div>
          <div className="m-value">{avgPerCollab}</div>
          <div className="m-sub">jours</div>
        </div>
        <div className="metric-card">
          <div className="m-label">Activités</div>
          <div className="m-value">{stats.numberOfActivities}</div>
        </div>
      </div>

      <div className="charts-grid" id="chartsGrid">
        <div className="chart-card" id="activityCard">
          <div className="card-title">Jours par type d&apos;activité</div>
          <BarChartBlock chartId="chartActivity" data={activityData} legendId="legendActivity" />
        </div>
        <div className="chart-card" id="clientCard">
          <div className="card-title">Jours par client</div>
          <BarChartBlock chartId="chartClient" data={clientData} legendId="legendClient" />
        </div>
      </div>

      <div className="chart-card" id="timelineCard" style={{ marginBottom: 16 }}>
        <div className="card-title">Évolution mensuelle</div>
        <TimelineChart data={timelineData} />
      </div>

      <div className="table-card" id="pivotCard">
        <div className="card-title">Tableau détaillé — Client × Activité × Jours</div>
        <div className="table-wrap">
          <table id="pivotTable" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '75%' }}>Client / Activité</th>
                <th style={{ width: '25%', textAlign: 'right' }}>Jours</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(pivot).map(([client, data]) => (
                  <Fragment key={client}>
                    <tr className={`row-client ${getStructureClass(client)}`}>
                      <td>{client}</td>
                      <td>{data.total.toFixed(1)}</td>
                    </tr>
                    {Object.entries(data.activities).map(([activity, hours]) => (
                      <tr key={`${client}-${activity}`} className="row-activity">
                        <td>{activity}</td>
                        <td>{hours.toFixed(1)}</td>
                      </tr>
                    ))}
                  </Fragment>
              ))}
              <tr className="row-grand-total">
                <td>Total général</td>
                <td>{pivotTotal.toFixed(1)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-card" id="collabCard">
        <div className="card-title">Récapitulatif par collaborateur</div>
        <div className="table-wrap">
          <table id="collabTable">
            <thead>
              <tr>
                <th>Collaborateur</th>
                <th>Structure</th>
                <th>Jours déclarés</th>
                <th>Nb clients</th>
              </tr>
            </thead>
            <tbody>
              {collabSummary.map((collab) => (
                <tr key={collab.name}>
                  <td style={{ fontWeight: 500 }}>{collab.name}</td>
                  <td dangerouslySetInnerHTML={{ __html: getStructureBadge(collab.structure) }} />
                  <td>{collab.hours.toFixed(1)}</td>
                  <td>{collab.clients}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <MissingDeclarations
        entries={entriesForMissing}
        structureId={currentStructure}
        startDate={filterFrom}
        endDate={filterTo}
      />
    </div>
  );
}



